import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Button, Typography } from '@altermaind/web/ui';
import { ErrorCodes, Routes } from '@/_shared/constants';
import { useDialog } from '@/_shared/hooks';
import { getModeTheme } from '@/_shared/styles/state/selectors';
import { useLogin } from '@/login/state';
import { DialogSwitchOrganization } from './DialogSwitchOrganization';

type Props = {
  error: Error;
  resetErrorBoundary: () => void;
};

const ERROR_WITHOUT_CTA = [
  ErrorCodes.USER_DISABLED,
  ErrorCodes.POPUP_BLOCKED,
  ErrorCodes.LICENSE_NOT_FOUND,
  ErrorCodes.USER_PENDING,
];
const ERROR_WITH_REDIRECT = [ErrorCodes.OAUTH_NO_WINDOW_OPENER];

export const FallbackError: FC<Props> = ({ resetErrorBoundary, error }) => {
  const t = useTranslations('errorBoundary');
  const tGeneral = useTranslations('general');
  const { openDialog } = useDialog('switchOrganizationDialog');
  const mode = useSelector(getModeTheme());
  const { logout } = useLogin();

  const handleReload = () => {
    resetErrorBoundary();
    window.location.href = Routes.Homepage;
  };

  const handleLogout = () => {
    resetErrorBoundary();
    logout();
  };

  useEffect(() => {
    if (error?.message && ERROR_WITH_REDIRECT.includes(error.message.toUpperCase() as never)) {
      handleLogout();
      window.location.href = Routes.Login;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Box
      bg='primary/background'
      height='100vh'
      width='100vw'
      flexDirection='column'
      display='flex'
      alignItems='center'
    >
      <Box
        minHeight='100vh'
        display='flex'
        flex='0 0 auto'
        alignItems='center'
        justifyContent='center'
        flexDirection='column'
      >
        <Asset
          mb='xl'
          domain='images'
          name={mode === 'light' ? 'logoLight' : 'logoDark'}
          width='150'
        />
        <Typography variant='h2' mb='xxs'>
          {t(`title.${error?.message}`, { defaultValue: t('title.default') })}
        </Typography>
        <Typography variant='p2/light' mb='xs'>
          {t(`description.${error?.message}`, { defaultValue: t('description.default') })}
        </Typography>
        {!ERROR_WITHOUT_CTA.includes(error?.message as never) && (
          <Button mt='s' onClick={handleReload}>
            {t('back')}
          </Button>
        )}
        {error?.message === ErrorCodes.USER_DISABLED && (
          <Button mt='s' onClick={handleLogout}>
            {tGeneral('logout')}
          </Button>
        )}
        {error?.message === ErrorCodes.USER_PENDING && (
          <>
            <Button variant='link/primary' mt='s' onClick={openDialog}>
              {t('switchOrganization')}
            </Button>
            <DialogSwitchOrganization />
          </>
        )}
      </Box>
    </Box>
  );
};
